import { useCallback, useRef, useState } from 'react';

import type { ITableColumn } from '../utils/types';
import { resolveColumnKey } from '../utils/resolveColumnKey';
import { DEFAULT_COLUMN_WIDTH } from '../utils/constants';

export interface IColumnWidthState {
  [columnKey: string]: number;
}

interface IResizeSession {
  columnKey: string;
  originalWidth: number;
}

export const useColumnWidths = <T>(
  columns: ITableColumn<T>[],
  onColumnResize?: (columnKey: string, width: number) => void
) => {
  const initialWidthState = columns.reduce<IColumnWidthState>((acc, col) => {
    const key = resolveColumnKey(col);
    if (key) {
      acc[key] = typeof col.width === 'number' ? col.width : (undefined as any);
    }
    return acc;
  }, {});

  const [widthState, setWidthState] =
    useState<IColumnWidthState>(initialWidthState);

  const resizeSessionRef = useRef<IResizeSession | null>(null);

  const startResize = useCallback(
    (columnKey: string) => {
      resizeSessionRef.current = {
        columnKey,
        originalWidth: widthState[columnKey] ?? DEFAULT_COLUMN_WIDTH,
      };
    },
    [widthState]
  );

  const setLiveWidth = useCallback((columnKey: string, width: number) => {
    setWidthState(prev => ({ ...prev, [columnKey]: width }));
  }, []);

  const saveWidth = useCallback(
    (columnKey: string, width: number) => {
      setWidthState(prev => ({ ...prev, [columnKey]: width }));
      onColumnResize?.(columnKey, width);
      resizeSessionRef.current = null;
    },
    [onColumnResize]
  );

  const cancelResize = useCallback(() => {
    const session = resizeSessionRef.current;
    if (!session) return;

    setWidthState(prev => ({
      ...prev,
      [session.columnKey]: session.originalWidth,
    }));
    resizeSessionRef.current = null;
  }, []);

  return {
    widthState,
    startResize,
    setLiveWidth,
    saveWidth,
    cancelResize,
  };
};
